<template>
    <div class="date-range row" data-test="date-range" :data-start-min="startMin" :data-end-min="endMin">
        <div class="col">
            <Field :name="startName" :label="startLabel" :error="startError">
                <Date
                    slot-scope="field"
                    v-model="startValue"
                    :min="minFrom"
                    :max="maxFrom"
                    :name="startName"
                    :highlighted="resolvedHighlited"
                    :disabled="resolvedDisabled"
                    :has-error="!!field.error"
                    :open-date="startMin || defaultOpenDate"
                    :inclusive="inclusive"
                    @input="handleStartDateChanged"
                />
            </Field>
        </div>
        <div class="col">
            <Field :name="endName" :label="endLabel" :error="endError">
                <Date
                    slot-scope="field"
                    v-model="endValue"
                    :min="minTo"
                    :max="maxTo"
                    :name="endName"
                    :highlighted="resolvedHighlited"
                    :disabled="resolvedDisabled"
                    :has-error="!!field.error"
                    :open-date="minTo || defaultOpenDate"
                    :inclusive="inclusive"
                    @input="handleEndDateChanged"
                />
            </Field>
        </div>
    </div>
</template>

<script>
    import DateComponent from './Date.vue';
    import Field from './Field.vue';
    import { addDays, resetHours, isValidDate } from "../../util/date";
    import eachDay from 'date-fns/each_day';
    import isSameDay from 'date-fns/is_same_day';


    const datePropsBase = {
        name: String,
        label: String,
        error: String,
        min: String,
        max: String
    };

    function capitalize(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    function createDateProps(target) {
        return Object.entries(datePropsBase).reduce((res,[prop,type]) => ({ ...res, [`${target}${capitalize(prop)}`]:type }), {});
    }
    // cast date if date return this, else null
    function dateOrDefault(date) {
        return isValidDate(date) ? date : null;
    }

    export default {
        components: {
            Date: DateComponent,
            Field
        },
        props: {
            start: String,
            end: String,
            disabledDays: {
                type: Array,
                default: ()=>[]
            },
            disabledDates: {
                type: Array,
                default: ()=>[]
            },
            maxDuration: {
                type: Number, //in days
            },
            inclusive: Boolean,
            ...createDateProps('start'),
            ...createDateProps('end'),
        },
        data() {
            return {
                startValue: this.start,
                endValue: this.end
            }
        },
        computed: {
            startMinDate: vm => vm.startMin ? new Date(vm.startMin) : -Infinity,
            startMaxDate: vm => vm.startMax ? new Date(vm.startMax) : Infinity,
            endMinDate: vm => vm.endMin ? new Date(vm.endMin) : -Infinity,
            endMaxDate: vm => vm.endMax ? new Date(vm.endMax) : Infinity,
            startDate: vm => new Date(vm.startValue),
            endDate: vm => new Date(vm.endValue),
            minFrom() {
                let durationLimitDate = this.maxDuration ? addDays(this.endDate, -this.maxDuration) : -Infinity;
                return durationLimitDate > this.startMinDate ? durationLimitDate : this.startMin;
            },
            maxFrom() {
                return this.startMax;
            },
            minTo() {
                return this.endMin;
            },
            maxTo() {
                let durationLimitDate = this.maxDuration ? addDays(this.startDate, this.maxDuration) : Infinity;
                return durationLimitDate < this.endMaxDate ? durationLimitDate : this.endMax;
            },
            resolvedDisabled() {
                return {
                    days: this.disabledDays,
                    dates: this.disabledDates.map(date=>new Date(date))
                }
            },
            resolvedHighlited() {
                return {
                    from: this.startValue ? this.startDate : null,
                    to:  this.endValue ? this.endDate : null
                }
            },
            defaultOpenDate() {
                return resetHours(new Date());
            }
        },
        methods: {
            handleStartDateChanged(start) {
                if(start && start > this.endDate) {
                    this.endValue = this.nextAvailableDate(start, addDays(start, this.maxDuration));
                }
            },
            handleEndDateChanged(end) {
                if(end && end < this.startDate) {
                    this.startValue = this.previousAvailableDate(end, this.startMin);
                }
            },
            isDisabledDay(date) {
                return (this.disabledDays||[]).some(disabledDay => date.getDay() === disabledDay);
            },
            isDisabledDate(date) {
                return (this.disabledDates||[]).some(disabledDate => isSameDay(date, disabledDate));
            },
            previousAvailableDate(date, min) {
                return isSameDay(date, min)
                    ? date
                    : (
                        eachDay(min, addDays(date, -1))
                            .reverse()
                            .find(date => !this.isDisabledDay(date) && !this.isDisabledDate(date))
                        || date
                    );
            },
            nextAvailableDate(date, max) {
                return isSameDay(date, max)
                    ? date
                    : (
                        eachDay(addDays(date, 1), max)
                            .find(date => !this.isDisabledDay(date) && !this.isDisabledDate(date))
                        || date
                    );
            },
        }
    }
</script>

<style>
    .highlight-start {
        border-left: 2px solid #4bd!important;
    }
    .highlight-end {
        border-right: 2px solid #4bd!important;
    }
</style>
